import { FC, useEffect, useState } from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import {
  loadSquadImageSize,
  squadPage3ImageStatus,
  type SquadImageSize,
  type SquadPage3ImageArea,
} from './squadPage3Image';

/**
 * Under the page-3 upload field: how the image there fits this factsheet's space, as
 * estimated in the form (see estimateSquadPage3ImageArea). Reads the image's pixel size
 * in the browser first.
 */
export const SquadPage3ImageFitStatus: FC<{ area: SquadPage3ImageArea; imageUrl?: string }> = ({
  area,
  imageUrl,
}) => {
  const url = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  const [loaded, setLoaded] = useState<{ url: string; size: SquadImageSize | null } | null>(null);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    loadSquadImageSize(url).then((size) => {
      if (!cancelled) setLoaded({ url, size });
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!url) return null;
  // A size read for the previous image must not be judged against the next one
  if (loaded?.url !== url) {
    return (
      <Typography variant="caption" color="text.secondary" role="status" sx={{ display: 'block', mt: -1, mb: 2 }}>
        Bildgröße wird geprüft …
      </Typography>
    );
  }
  if (!loaded.size) {
    return (
      <Typography variant="caption" color="text.secondary" role="status" sx={{ display: 'block', mt: -1, mb: 2 }}>
        Die Bildgröße ließ sich nicht lesen – bitte in der PDF-Vorschau prüfen.
      </Typography>
    );
  }
  const lines = squadPage3ImageStatus(area, loaded.size);
  if (!lines.length) return null;
  return (
    <Stack spacing={1} sx={{ mt: -1, mb: 2 }}>
      {lines.map((line) => (
        // A fit is news, not an alarm
        <Alert key={line.text} severity={line.severity} role={line.severity === 'warning' ? 'alert' : 'status'}>
          {line.text}
        </Alert>
      ))}
    </Stack>
  );
};
